// The Dock's row and its buttons: the entries (Me's, a line, the place's) and
// "⋯", the round button the Dock folds to, and the way up.
//
// Promise:
// - The entries share the row's width evenly (each at least DOCK_ITEM_WIDTH);
//   the current one has a ground behind its icon (never bold), and one that
//   has something to do a dot at its corner (no count).
// - "⋯" opens the menu, for a finger at the press (also when iOS sends no
//   click for the tap, ui/press.ts). It carries a dot when an entry not in
//   the Dock has something to do. When the current screen is not in the Dock, "⋯" shows
//   its icon instead (with the up-down mark), so the row always says where
//   the person is.
// - Pressing a button never moves the focus (iOS moved it to the enclosing
//   element and scrolled the page, 2026-09-28).
import { Link, useMatchRoute, useRouter } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useContext,
} from "react";
import { useBackTo } from "../shell/list-memory";
import { useShellDockTool } from "../shell/outside/outside";
import type { DockButtonProps } from "../shell/phone";
import { GLASS } from "../ui/glass";
import { Icon } from "../ui/icon";
import { openOnPress } from "../ui/press";
import { HostedDock } from "./context";
import { DOCK_ITEM_WIDTH, type DockEntry } from "./items";

/** The Dock's height and its round buttons' (iOS Safari's own bottom bar). */
export const DOCK_HEIGHT = 48;

const keepFocus = (e: MouseEvent) => e.preventDefault();

/**
 * The entry the screen shows now: the longest `to` (or one of its `paths`)
 * the location is under.
 */
export function useHere(entries: readonly DockEntry[]): DockEntry | null {
  const match = useMatchRoute();
  let best: DockEntry | null = null;
  let bestLength = -1;
  for (const e of entries)
    for (const to of [e.to, ...(e.paths ?? [])])
      if (match({ to, fuzzy: true }) !== false && to.length > bestLength) {
        best = e;
        bestLength = to.length;
      }
  return best;
}

/** A dot at a button's corner (laid over it: the button keeps its size). */
export function UnreadDot({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-dock-dot=""
      className={`pointer-events-none absolute top-[9px] right-[9px] size-[7px] rounded-full bg-accent ${className}`}
    />
  );
}

/** The ground of the current entry: a pill inside its cell. */
function CurrentGround() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-[3px] inset-y-1 -z-10 rounded-full bg-glass-current"
    />
  );
}

/** A cell of the row (its least width is DOCK_ITEM_WIDTH, set inline). */
const CELL =
  "relative isolate flex h-12 flex-1 cursor-pointer items-center justify-center text-fg glass-icon";

function BadgeOf({
  entry,
  children,
}: {
  entry: DockEntry;
  children(dot: boolean): ReactNode;
}) {
  const badge = entry.useBadge?.();
  return (
    <>{children(badge !== undefined && badge !== null && badge !== false)}</>
  );
}

/**
 * Calls an entry's badge hook inside its scope (a workspace's sections need
 * the workspace's context, also in Me) and draws `children(dot)`.
 */
export function EntryBadge({
  entry,
  children,
}: {
  entry: DockEntry;
  children(dot: boolean): ReactNode;
}) {
  const inner = <BadgeOf entry={entry}>{children}</BadgeOf>;
  return entry.Scope ? <entry.Scope>{inner}</entry.Scope> : inner;
}

function EntryButton({
  entry,
  current,
  onGo,
}: {
  entry: DockEntry;
  current: boolean;
  onGo?: () => void;
}) {
  return (
    <EntryBadge entry={entry}>
      {(dot) => (
        <Link
          to={entry.to}
          aria-label={
            dot ? m.phone_unread_named({ label: entry.label }) : entry.label
          }
          aria-current={current ? "page" : undefined}
          onMouseDown={keepFocus}
          onClick={onGo}
          className={CELL}
          style={{ minWidth: DOCK_ITEM_WIDTH }}
        >
          {current && <CurrentGround />}
          <entry.icon className="size-5" />
          {dot && <UnreadDot />}
        </Link>
      )}
    </EntryBadge>
  );
}

/** Says, inside "⋯", that an entry outside the Dock has something to do (CSS draws the dot). */
function BadgeProbe({ entry }: { entry: DockEntry }) {
  return (
    <EntryBadge entry={entry}>
      {(dot) => (dot ? <span hidden data-dock-badge="" /> : null)}
    </EntryBadge>
  );
}

/**
 * One Dock row: its entries, the line between Me's and the place's, and "⋯".
 * The same row rests at the bottom, grows from the round button, and sits under
 * the menu's panel.
 */
export function DockRow({
  dock,
  all,
  here,
  panel,
  onMore,
  moreRef,
  onGo,
  width,
}: {
  dock: readonly DockEntry[];
  all: readonly DockEntry[];
  here: DockEntry | null;
  /** The menu's panel is open ("⋯" reads as expanded). */
  panel: boolean;
  /** "⋯": whether a finger or the mouse pressed it (not the keyboard), and the button. */
  onMore(pointer: boolean, button: HTMLButtonElement): void;
  moreRef?: Ref<HTMLButtonElement>;
  /** After an entry is chosen. */
  onGo?: () => void;
  /** A fixed width (the row does not reflow while its surface grows). */
  width?: number;
}) {
  const inDock = new Set(dock.map((e) => e.key));
  const hereOutside = here !== null && !inDock.has(here.key) ? here : null;
  const me = dock.filter((e) => e.group === "me");
  const place = dock.filter((e) => e.group === "place");
  const moreLabel = hereOutside
    ? m.phone_more_current({ label: hereOutside.label })
    : m.phone_more();
  return (
    <div
      className={`flex h-12 flex-none items-center px-1 ${width === undefined ? "w-full" : ""}`}
      style={width === undefined ? undefined : { width }}
    >
      {me.map((e) => (
        <EntryButton
          key={e.key}
          entry={e}
          current={here?.key === e.key}
          onGo={onGo}
        />
      ))}
      {/* The line takes no width: the cells keep the old web's shape (52 by
          48 on a 390 screen), which a line of its own room made narrower
          (the person, 2026-10-18). */}
      {me.length > 0 && place.length > 0 && (
        <span
          aria-hidden="true"
          data-dock-line=""
          className="relative h-5 w-0 flex-none"
        >
          <span className="absolute inset-y-0 -left-px w-px bg-field" />
        </span>
      )}
      {place.map((e) => (
        <EntryButton
          key={e.key}
          entry={e}
          current={here?.key === e.key}
          onGo={onGo}
        />
      ))}
      <button
        ref={moreRef}
        type="button"
        aria-label={moreLabel}
        aria-haspopup="dialog"
        aria-expanded={panel}
        aria-current={hereOutside ? "page" : undefined}
        // Opening, at the press for a finger: iOS dropped the click of the
        // first tap after the app opened (ui/press.ts). Closing the panel, by
        // the click: closed at the press, the menu is out of reach at once
        // and the tap's mousedown went to the row under it.
        {...(panel
          ? {
              onMouseDown: keepFocus,
              onClick: (e: MouseEvent<HTMLButtonElement>) =>
                onMore(e.detail > 0, e.currentTarget),
            }
          : openOnPress((button, pointer) => onMore(pointer, button)))}
        className={`group ${CELL}`}
        style={{ minWidth: DOCK_ITEM_WIDTH }}
      >
        {(panel || hereOutside) && <CurrentGround />}
        {hereOutside ? (
          <>
            <hereOutside.icon className="size-5" />
            {/* Laid over, so the icon stays in the cell's middle like the others. */}
            <span
              aria-hidden="true"
              className="absolute top-1/2 left-[calc(50%+8px)] flex -translate-y-1/2 text-faint"
            >
              <Icon kind="updown" size={12} />
            </span>
          </>
        ) : (
          <Icon kind="more" size={20} />
        )}
        {all
          .filter((e) => !inDock.has(e.key) && e.key !== here?.key)
          .map((e) => (
            <BadgeProbe key={e.key} entry={e} />
          ))}
        <UnreadDot className="hidden group-has-[[data-dock-badge]]:block" />
      </button>
    </div>
  );
}

/** A surface of glass the Dock's buttons sit on (one surface for the row). */
export function DockSurface({
  className = "",
  children,
  ...rest
}: {
  className?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement> &
  Record<`data-${string}`, string | undefined>) {
  return (
    <div
      {...rest}
      className={`${GLASS} pointer-events-auto relative flex items-center rounded-[24px] ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * The round button the Dock folds to (where the page holds the Dock's row):
 * the current screen's icon, a dot when there is something to do elsewhere.
 */
export function RoundMenuButton({
  ref,
  here,
  others,
  open,
  onOpen,
  bare = false,
}: {
  ref?: Ref<HTMLButtonElement>;
  here: DockEntry | null;
  /** The entries that are not the current screen (their dots). */
  others: readonly DockEntry[];
  open: boolean;
  /** Whether a finger or the mouse pressed it, and the button. */
  onOpen(pointer: boolean, button: HTMLButtonElement): void;
  /** Inside a surface already (a page's buttons): no glass of its own. */
  bare?: boolean;
}) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={m.phone_menu_open()}
      aria-haspopup="dialog"
      aria-expanded={open}
      // At the press for a finger, as "⋯" (ui/press.ts).
      {...openOnPress((button, pointer) => onOpen(pointer, button))}
      // While the Dock grown from it is open it is gone (the old web's): the
      // glass over it is see-through, so it showed under the Dock.
      className={`group relative isolate flex h-12 flex-none cursor-pointer items-center justify-center text-fg glass-icon ${
        bare ? "" : `${GLASS} pointer-events-auto rounded-[24px]`
      } ${open ? "opacity-0" : ""}`}
      style={{ width: DOCK_ITEM_WIDTH }}
    >
      {here ? <here.icon className="size-5" /> : <Icon kind="more" size={20} />}
      {others.map((e) => (
        <BadgeProbe key={e.key} entry={e} />
      ))}
      <UnreadDot className="hidden group-has-[[data-dock-badge]]:block" />
    </button>
  );
}

const PAGE_BUTTON =
  "relative isolate flex h-12 w-[49px] flex-none cursor-pointer items-center justify-center text-fg glass-icon disabled:cursor-default disabled:opacity-40";

/** A page's round button on the Dock (on the page's buttons' surface). */
export function PageDockButton({
  label,
  icon,
  to,
  params,
  onClick,
  disabled,
  ref,
  expanded,
  tool,
}: DockButtonProps) {
  const hosted = useContext(HostedDock);
  const router = useRouter();
  useShellDockTool(
    hosted && tool
      ? {
          id: tool.id,
          label,
          icon: tool.icon,
          ...(disabled ? { disabled: true } : {}),
          ...(tool.items ? { items: tool.items } : {}),
          ...(tool.order !== undefined ? { order: tool.order } : {}),
          onPress: to
            ? () => void router.navigate({ to, params } as never)
            : onClick,
        }
      : null,
  );
  if (hosted) return null;
  if (to)
    return (
      <Link
        to={to as never}
        params={params as never}
        aria-label={label}
        onMouseDown={keepFocus}
        className={PAGE_BUTTON}
      >
        {icon}
      </Link>
    );
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      disabled={disabled}
      onMouseDown={keepFocus}
      onClick={onClick}
      className={PAGE_BUTTON}
    >
      {icon}
    </button>
  );
}

/** The Dock's way up to the parent (the left slot). */
export function DockUpButton({ to, label }: { to: string; label: string }) {
  // Up to the page it came from is back (the list comes back where it was).
  const backTo = useBackTo();
  return (
    <button
      type="button"
      aria-label={label}
      onMouseDown={keepFocus}
      onClick={() => backTo(to)}
      className={`${GLASS} glass-icon pointer-events-auto flex h-12 flex-none cursor-pointer items-center justify-center rounded-[24px] text-fg`}
      style={{ width: DOCK_ITEM_WIDTH }}
    >
      {/* Not the top left's back: one level up looks apart from back in
          history (the person, 2026-10-18). */}
      <Icon kind="up" size={20} />
    </button>
  );
}
